import React, { useMemo, useState, useEffect } from "react"
import { Button, Card, CardBody, Col, Row } from "reactstrap"
import Select from "react-select"
import ReactApexChart from "react-apexcharts"
import { FaFilter } from "react-icons/fa"
import Breadcrumb from "components/Common/Breadcrumb"
import { DatePicker } from "antd"
import { customStyles } from "utils/SelectStyles"
import secureLocalStorage from "react-secure-storage"
import dayjs from "dayjs"
import { FetchPostData } from "utils/Common_api_functions"
import Loader from "components/Loader/loader"

const { RangePicker } = DatePicker

// ── Category mapping (image: subCategory → name) ─────────────────────────────
// 4.3 = Bacon | 5.1 = Cured | 2.x = Fresh Pork | 4.5 = Ham
// 3.x = Puddings | 1.x (excl 1.7) = Sausages | 1.7 = Hafners | Direct Sales removed

const CATEGORIES = [
  { label: "Bacon", value: "Bacon" },
  { label: "Cured", value: "Cured" },
  { label: "Fresh Pork", value: "Fresh Pork" },
  { label: "Ham", value: "Ham" },
  { label: "Puddings", value: "Puddings" },
  { label: "Sausages", value: "Sausages" },
  { label: "Hafners", value: "Hafners" },
]

const getCategoryName = (category, subCategory) => {
  const sc = String(subCategory || "")
  const cat = String(category || "")
  if (sc.startsWith("4.3")) return "Bacon"
  if (sc.startsWith("5.1")) return "Cured"
  if (sc.startsWith("4.5")) return "Ham"
  if (sc.startsWith("1.7")) return "Hafners"
  if (cat === "2") return "Fresh Pork"
  if (cat === "3") return "Puddings"
  if (cat === "1") return "Sausages"
  return null
}

const aggregateRecords = (records, startDate, endDate) => {
  const arr = Array.isArray(records) ? records : Object.values(records || {})
  const start = startDate ? dayjs(startDate).format("YYYY-MM-DD") : null
  const end = endDate ? dayjs(endDate).format("YYYY-MM-DD") : null
  const map = {}
  arr.forEach(r => {
    if (start && end) {
      const d = String(r.date || "").slice(0, 10)
      if (d < start || d > end) return
    }
    const name = getCategoryName(r.category, r.subCategory)
    if (!name) return
    if (!map[name]) map[name] = { category: name, kgs: 0, salesValue: 0 }
    map[name].kgs += Number(r.weeklyWeight || 0)
    map[name].salesValue += Number(r.weeklySalesValue || 0)
  })
  return map
}

const fmtGBP = v =>
  "£ " + Math.abs(v).toLocaleString("en-GB", { maximumFractionDigits: 0 })

const pctChange = (curr, prev) => {
  if (!prev) return null
  return (((curr - prev) / prev) * 100).toFixed(0)
}

const SalesReport = () => {
  const [thisWeekRange, setThisWeekRange] = useState([])
  const [prevWeekRange, setPrevWeekRange] = useState([])
  const [thisWeekHover, setThisWeekHover] = useState(null)
  const [prevWeekHover, setPrevWeekHover] = useState(null)
  const [currentWeek, setCurrentWeek] = useState([])
  const [selectedCategories, setSelectedCategories] = useState([])
  const [loading, setLoading] = useState(false)
  const [reportData, setReportData] = useState(null)

  useEffect(() => {
    const DateRange = secureLocalStorage?.getItem("DateRange")
    const FinalRangeRaw = secureLocalStorage?.getItem("FINALRANGE")
    const activeDateRaw = DateRange ?? FinalRangeRaw
    const parsed = activeDateRaw ? activeDateRaw.map(d => dayjs(d)) : []
    setThisWeekRange(parsed)
    setCurrentWeek(parsed)
  }, [])

  const makeDisabledDate = hoverDate => current => {
    if (!hoverDate) return false
    return Math.abs(current.diff(hoverDate, "days")) >= 7
  }

  const handleFilter = async () => {
    const res = await FetchPostData(setLoading, "sheet/sales/report", {
      startDate: thisWeekRange?.[0]?.format("YYYY-MM-DD"),
      endDate: thisWeekRange?.[1]?.format("YYYY-MM-DD"),
      previousStartDate: prevWeekRange?.[0]?.format("YYYY-MM-DD"),
      previousEndDate: prevWeekRange?.[1]?.format("YYYY-MM-DD"),
    })
    if (res?.data?.success) setReportData(res.data)
    else setReportData(null)
  }

  const tableData = useMemo(() => {
    if (!reportData) return []
    const allRecords = reportData.data || reportData
    const thisMap = aggregateRecords(
      allRecords,
      thisWeekRange?.[0],
      thisWeekRange?.[1]
    )
    const prevMap = aggregateRecords(
      allRecords,
      prevWeekRange?.[0],
      prevWeekRange?.[1]
    )

    const allNames = new Set([...Object.keys(thisMap), ...Object.keys(prevMap)])
    return Array.from(allNames).map(name => ({
      category: name,
      kgs: thisMap[name]?.kgs ?? 0,
      lastKgs: prevMap[name]?.kgs ?? 0,
      thisWeek: thisMap[name]?.salesValue ?? 0,
      lastWeek: prevMap[name]?.salesValue ?? 0,
    }))
  }, [reportData, thisWeekRange, prevWeekRange])

  const filtered = useMemo(() => {
    const base = tableData
    if (!selectedCategories.length) return base
    const vals = new Set(selectedCategories.map(c => c.value))
    return base.filter(r => vals.has(r.category))
  }, [tableData, selectedCategories])

  const totals = useMemo(
    () => ({
      kgs: filtered.reduce((s, r) => s + r.kgs, 0),
      lastKgs: filtered.reduce((s, r) => s + r.lastKgs, 0),
      thisWeek: filtered.reduce((s, r) => s + r.thisWeek, 0),
      lastWeek: filtered.reduce((s, r) => s + r.lastWeek, 0),
    }),
    [filtered]
  )

  // ── Bar Chart (This Week vs Last Week) ───────────────────────────────────
  const barOptions = {
    chart: { type: "bar", toolbar: { show: false }, background: "transparent" },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "55%",
        borderRadius: 4,
        grouped: true,
      },
    },
    colors: ["#4361ee", "#4CBB17"],
    dataLabels: { enabled: false },
    xaxis: {
      categories: filtered.map(r => r.category),
      labels: {
        rotate: -35,
        style: { fontSize: "11px", fontWeight: 600, colors: "#374151" },
      },
    },
    yaxis: {
      title: { text: "£ Value", style: { fontSize: "12px", fontWeight: 700 } },
      labels: { formatter: v => `£${Math.round(v / 1000)}K` },
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: { formatter: v => fmtGBP(v) },
    },
    legend: { position: "top", fontSize: "12px", fontWeight: 600 },
    grid: { borderColor: "#e8e8f0", strokeDashArray: 4 },
  }

  const thisWeekLabel = thisWeekRange?.[0]
    ? `This Week (${thisWeekRange[0].format("DD/MM/YYYY")})`
    : "This Week"
  const prevWeekLabel = prevWeekRange?.[0]
    ? `Last Week (${prevWeekRange[0].format("DD/MM/YYYY")})`
    : "Last Week"

  const barSeries = [
    { name: thisWeekLabel, data: filtered.map(r => r.thisWeek) },
    { name: prevWeekLabel, data: filtered.map(r => r.lastWeek) },
  ]

  // ── Donut Chart (This Week share) ────────────────────────────────────────
  const donutColors = [
    "#4361ee",
    "#4CBB17",
    "#f59e0b",
    "#ef4444",
    "#8b5cf6",
    "#10b981",
    "#ec4899",
    "#0ea5e9",
    "#f97316",
    "#6366f1",
  ]
  const donutOptions = {
    chart: { type: "donut", toolbar: { show: false } },
    labels: filtered.map(r => r.category),
    colors: donutColors.slice(0, filtered.length),
    legend: { position: "bottom", fontSize: "11px", fontWeight: 600 },
    dataLabels: { enabled: true, formatter: v => `${v.toFixed(1)}%` },
    tooltip: { y: { formatter: v => fmtGBP(v) } },
    plotOptions: { pie: { donut: { size: "60%" } } },
  }
  const donutSeries = filtered.map(r => r.thisWeek)

  // ── Horizontal Bar (Kgs) ─────────────────────────────────────────────────
  const kgsOptions = {
    chart: { type: "bar", toolbar: { show: false }, background: "transparent" },
    plotOptions: {
      bar: { horizontal: true, borderRadius: 4, barHeight: "60%" },
    },
    colors: ["#8b5cf6"],
    dataLabels: {
      enabled: true,
      formatter: v => `${v.toLocaleString("en-GB")} kg`,
      style: { fontSize: "10px", fontWeight: 700 },
    },
    xaxis: {
      title: {
        text: "Weight (Kgs)",
        style: { fontSize: "12px", fontWeight: 700 },
      },
      labels: { formatter: v => `${v.toLocaleString("en-GB")}` },
    },
    yaxis: {
      labels: {
        style: { fontSize: "11px", fontWeight: 600, colors: "#374151" },
      },
    },
    tooltip: { y: { formatter: v => `${v.toLocaleString("en-GB")} kgs` } },
    grid: { borderColor: "#e8e8f0", strokeDashArray: 4 },
  }
  const kgsSeries = [
    {
      name: "Weight (Kgs)",
      data: filtered
        .filter(r => r.kgs > 0)
        .map(r => ({ x: r.category, y: r.kgs })),
    },
  ]

  return (
    <div className="page-content">
      {loading && <Loader />}
      <div className="container-fluid">
        <Breadcrumb title="Sales Report" breadcrumbItem="Sales Report" />

        {/* ── Filter Header ── */}
        <div
          style={{
            background: "linear-gradient(135deg, #ffffff 0%, #f8faff 100%)",
            borderRadius: "16px",
            border: "1px solid #ede9fe",
            boxShadow: "0 4px 24px rgba(108,99,255,0.07)",
            padding: "16px 20px",
            marginBottom: "24px",
            position: "relative",
          }}
        >
          <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <div>
                <h4
                  style={{
                    margin: 0,
                    fontSize: "18px",
                    fontWeight: 700,
                    color: "#000",
                  }}
                >
                  Sales Report
                </h4>
                <p style={{ margin: 0, fontSize: "12px", color: "#666" }}>
                  Weekly category breakdown &amp; comparison
                </p>
              </div>
              <div
                style={{ width: "1px", height: "36px", background: "#e2e8f0" }}
              />

              {currentWeek?.[0] && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    background: "#f0fdf4",
                    border: "1px solid #bbf7d0",
                    borderRadius: "10px",
                    padding: "6px 12px",
                  }}
                >
                  <span
                    style={{
                      width: "7px",
                      height: "7px",
                      borderRadius: "50%",
                      background: "#16a34a",
                      display: "inline-block",
                      boxShadow: "0 0 0 2px #bbf7d0",
                    }}
                  />
                  <div>
                    <div
                      style={{
                        fontSize: "10px",
                        color: "#15803d",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.6px",
                      }}
                    >
                      Latest week Upload
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#166534",
                        fontWeight: 600,
                        marginTop: "1px",
                      }}
                    >
                      {currentWeek[0].format("DD MMM")} —{" "}
                      {currentWeek[1]?.format("DD MMM YYYY")}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="d-flex align-items-end gap-2 flex-wrap">
              <Select
                placeholder="Category"
                options={CATEGORIES}
                styles={customStyles}
                isMulti
                value={selectedCategories}
                onChange={v => setSelectedCategories(v || [])}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
              <div
                style={{ display: "flex", flexDirection: "column", gap: "2px" }}
              >
                <span
                  style={{
                    fontSize: "10px",
                    fontWeight: 700,
                    color: "#059669",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    paddingLeft: "2px",
                  }}
                >
                  This Week
                </span>
                <RangePicker
                  value={thisWeekRange}
                  placeholder={["Start", "End"]}
                  onChange={vals => setThisWeekRange(vals || [])}
                  onCalendarChange={vals => setThisWeekHover(vals?.[0] ?? null)}
                  disabledDate={makeDisabledDate(thisWeekHover)}
                  format="DD-MM-YYYY"
                  style={{ borderRadius: "8px", height: "38px" }}
                />
              </div>
              <div
                style={{ display: "flex", flexDirection: "column", gap: "2px" }}
              >
                <span
                  style={{
                    fontSize: "10px",
                    fontWeight: 700,
                    color: "#f59e0b",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    paddingLeft: "2px",
                  }}
                >
                  Previous Week
                </span>
                <RangePicker
                  value={prevWeekRange}
                  placeholder={["Start", "End"]}
                  onChange={vals => setPrevWeekRange(vals || [])}
                  onCalendarChange={vals => setPrevWeekHover(vals?.[0] ?? null)}
                  disabledDate={makeDisabledDate(prevWeekHover)}
                  format="DD-MM-YYYY"
                  style={{ borderRadius: "8px", height: "38px" }}
                />
              </div>
              <Button
                onClick={handleFilter}
                className="btn-blue rounded-2 border-transparent"
                style={{ marginTop: "18px" }}
              >
                <FaFilter size={12} />
              </Button>
            </div>
          </div>
        </div>

        {/* ── Main Table ── */}
        <Card
          className="mb-3"
          style={{ borderRadius: "12px", border: "1px solid #e2e8f0" }}
        >
          <CardBody>
            <h6
              style={{
                fontWeight: 700,
                color: "#1a1a2e",
                marginBottom: "14px",
                fontSize: "14px",
              }}
            >
              Weekly Sales Breakdown by Category
            </h6>
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "separate",
                  borderSpacing: 0,
                  fontSize: "13px",
                }}
              >
                <thead>
                  <tr style={{ background: "#1a1a2e" }}>
                    {[
                      "Category",
                      "This Week Kgs",
                      "Prev Week Kgs",
                      "% Change (Kgs)",
                      "This Week (£)",
                      "Last Week (£)",
                      "% Change (£)",
                    ].map((h, i) => (
                      <th
                        key={i}
                        style={{
                          padding: "10px 14px",
                          color: "#fff",
                          fontWeight: 700,
                          textAlign: i === 0 ? "left" : "right",
                          whiteSpace: "nowrap",
                          borderBottom: "2px solid #334155",
                          borderRight: i < 6 ? "1px solid #334155" : "none",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((row, idx) => {
                    const pct = pctChange(row.thisWeek, row.lastWeek)
                    const isPos = pct !== null && Number(pct) >= 0
                    const kgsPct = pctChange(row.kgs, row.lastKgs)
                    const kgsPos = kgsPct !== null && Number(kgsPct) >= 0
                    return (
                      <tr
                        key={idx}
                        style={{
                          background: idx % 2 === 0 ? "#f8fafc" : "#fff",
                        }}
                      >
                        <td
                          style={{
                            padding: "9px 14px",
                            fontWeight: 600,
                            color: "#1a1a2e",
                            borderBottom: "1px solid #e2e8f0",
                            borderRight: "1px solid #e2e8f0",
                          }}
                        >
                          {row.category}
                        </td>
                        <td
                          style={{
                            padding: "9px 14px",
                            textAlign: "right",
                            borderBottom: "1px solid #e2e8f0",
                            borderRight: "1px solid #e2e8f0",
                            color: "#374151",
                          }}
                        >
                          {row.kgs > 0 ? row.kgs.toLocaleString("en-GB") : "0"}
                        </td>
                        <td
                          style={{
                            padding: "9px 14px",
                            textAlign: "right",
                            borderBottom: "1px solid #e2e8f0",
                            borderRight: "1px solid #e2e8f0",
                            color: "#374151",
                          }}
                        >
                          {row.lastKgs > 0
                            ? row.lastKgs.toLocaleString("en-GB")
                            : "0"}
                        </td>
                        <td
                          style={{
                            padding: "9px 14px",
                            textAlign: "right",
                            borderBottom: "1px solid #e2e8f0",
                            borderRight: "1px solid #e2e8f0",
                            fontWeight: 700,
                            color:
                              kgsPct === null
                                ? "#9ca3af"
                                : kgsPos
                                ? "#15803d"
                                : "#be123c",
                          }}
                        >
                          {kgsPct !== null
                            ? `${kgsPos ? "▲" : "▼"} ${Math.abs(kgsPct)}%`
                            : "—"}
                        </td>
                        <td
                          style={{
                            padding: "9px 14px",
                            textAlign: "right",
                            borderBottom: "1px solid #e2e8f0",
                            borderRight: "1px solid #e2e8f0",
                            fontWeight: 700,
                            color: "#059669",
                          }}
                        >
                          {fmtGBP(row.thisWeek)}
                        </td>
                        <td
                          style={{
                            padding: "9px 14px",
                            textAlign: "right",
                            borderBottom: "1px solid #e2e8f0",
                            borderRight: "1px solid #e2e8f0",
                            color: "#374151",
                          }}
                        >
                          {row.lastWeek > 0 ? fmtGBP(row.lastWeek) : "0"}
                        </td>
                        <td
                          style={{
                            padding: "9px 14px",
                            textAlign: "right",
                            borderBottom: "1px solid #e2e8f0",
                            fontWeight: 700,
                            color:
                              pct === null
                                ? "#9ca3af"
                                : isPos
                                ? "#15803d"
                                : "#be123c",
                          }}
                        >
                          {pct !== null
                            ? `${isPos ? "▲" : "▼"} ${Math.abs(pct)}%`
                            : "0"}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ background: "#1e293b" }}>
                    <td
                      style={{
                        padding: "10px 14px",
                        color: "#fff",
                        fontWeight: 800,
                        borderTop: "2px solid #334155",
                      }}
                    >
                      Grand Total
                    </td>
                    <td
                      style={{
                        padding: "10px 14px",
                        color: "#e2e8f0",
                        fontWeight: 700,
                        textAlign: "right",
                        borderTop: "2px solid #334155",
                      }}
                    >
                      {totals.kgs.toLocaleString("en-GB")}
                    </td>
                    <td
                      style={{
                        padding: "10px 14px",
                        color: "#e2e8f0",
                        fontWeight: 700,
                        textAlign: "right",
                        borderTop: "2px solid #334155",
                      }}
                    >
                      {totals.lastKgs.toLocaleString("en-GB")}
                    </td>
                    <td
                      style={{
                        padding: "10px 14px",
                        fontWeight: 800,
                        textAlign: "right",
                        borderTop: "2px solid #334155",
                        color:
                          totals.kgs >= totals.lastKgs ? "#6ee7b7" : "#fca5a5",
                      }}
                    >
                      {totals.lastKgs
                        ? `${
                            totals.kgs >= totals.lastKgs ? "▲" : "▼"
                          } ${Math.abs(pctChange(totals.kgs, totals.lastKgs))}%`
                        : "—"}
                    </td>
                    <td
                      style={{
                        padding: "10px 14px",
                        color: "#6ee7b7",
                        fontWeight: 800,
                        textAlign: "right",
                        borderTop: "2px solid #334155",
                      }}
                    >
                      {fmtGBP(totals.thisWeek)}
                    </td>
                    <td
                      style={{
                        padding: "10px 14px",
                        color: "#fde68a",
                        fontWeight: 800,
                        textAlign: "right",
                        borderTop: "2px solid #334155",
                      }}
                    >
                      {totals.lastWeek > 0 ? fmtGBP(totals.lastWeek) : "—"}
                    </td>
                    <td
                      style={{
                        padding: "10px 14px",
                        color:
                          totals.thisWeek >= totals.lastWeek
                            ? "#6ee7b7"
                            : "#fca5a5",
                        fontWeight: 800,
                        textAlign: "right",
                        borderTop: "2px solid #334155",
                      }}
                    >
                      {totals.lastWeek
                        ? `${
                            totals.thisWeek >= totals.lastWeek ? "▲" : "▼"
                          } ${Math.abs(
                            pctChange(totals.thisWeek, totals.lastWeek)
                          )}%`
                        : "—"}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardBody>
        </Card>

        {/* ── Charts Row 1: Bar + Donut ── */}
        <Row className="mb-3 g-3">
          <Col xs={12} lg={7}>
            <Card
              style={{
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
                height: "100%",
              }}
            >
              <CardBody>
                <h6
                  style={{
                    fontWeight: 700,
                    color: "#1a1a2e",
                    marginBottom: "4px",
                    fontSize: "14px",
                  }}
                >
                  This Week vs Last Week — £ Value
                </h6>
                <ReactApexChart
                  options={barOptions}
                  series={barSeries}
                  type="bar"
                  height={320}
                />
              </CardBody>
            </Card>
          </Col>
          <Col xs={12} lg={5}>
            <Card
              style={{
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
                height: "100%",
              }}
            >
              <CardBody>
                <h6
                  style={{
                    fontWeight: 700,
                    color: "#1a1a2e",
                    marginBottom: "4px",
                    fontSize: "14px",
                  }}
                >
                  This Week — Category Share
                </h6>
                <ReactApexChart
                  options={donutOptions}
                  series={donutSeries}
                  type="donut"
                  height={320}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* ── Charts Row 2: Kgs Horizontal Bar ── */}
        <Row className="mb-3 g-3">
          <Col xs={12}>
            <Card style={{ borderRadius: "12px", border: "1px solid #e2e8f0" }}>
              <CardBody>
                <h6
                  style={{
                    fontWeight: 700,
                    color: "#1a1a2e",
                    marginBottom: "4px",
                    fontSize: "14px",
                  }}
                >
                  Weight by Category (Kgs)
                </h6>
                <ReactApexChart
                  options={kgsOptions}
                  series={kgsSeries}
                  type="bar"
                  height={280}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  )
}

export default SalesReport
